import { useState, useRef } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import Badge from '@/components/ui/Badge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorMessage from '@/components/common/ErrorMessage';
import { useInventoryRecords, useInventoryCount, useUploadInventory } from '@/hooks/useInventory';
import type { CsvLoadResult } from '@/hooks/useInventory';

export default function AdminInventory() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [uploadResult, setUploadResult] = useState<CsvLoadResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: recordsData, isLoading, error } = useInventoryRecords(page, pageSize);
  const { data: count } = useInventoryCount();
  const uploadMutation = useUploadInventory();

  const records = recordsData?.records || [];
  const totalPages = count ? Math.ceil(count / pageSize) : 0;

  const handleDownloadTemplate = () => {
    const headers = 'UNIQUE_ID,OMS_IDENTIFIER,LOCATION_DESCRIPTION,MEDIA_FORMAT,LATITUDE,LONGITUDE,IS_DIGITAL,UNIT,MARKET,SIZE,ILLUMINATION,ILLUMINATION_HOURS,FACING,PRIMARY_READ,#FACES,NET_INSTALL_COST,NET_COPY_CHANGE_COST,NET_PRODUCTION_COST,PRODUCTION_FORCED,NET_RATE,RATE_CYCLE,DIGITAL_SPOT_LENGTH,NUMBER_OF_SPOTS,IMAGE_URL,SPEC_SHEET_URL,18+_IMPRESSIONS,MARKETING_INFO,PRODUCTION_SPEC,PACKAGE_NAME,RESTRICTIONS,ENABLED_FOR_AUTOMATION,ACTIVE_DATE,INACTIVE_DATE';
    const blob = new Blob([headers + '\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'inventory-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadResult(null);
    setUploadError(null);
    uploadMutation.mutate(file, {
      onSuccess: (data) => {
        setUploadResult(data);
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
      onError: (error: unknown) => {
        const err = error as Error & { response?: { data?: { error?: string } }; message?: string };
        setUploadError(err?.response?.data?.error || err?.message || 'Upload failed');
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
    });
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message="Failed to load inventory records" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h1 className="text-2xl font-bold text-neutral-900">Inventory Management</h1>
        <Button variant="outline" onClick={handleDownloadTemplate}>
          Download Template
        </Button>
      </div>

      {/* Upload Section */}
      <Card className="p-4">
        <div className="flex items-center gap-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            disabled={uploadMutation.isPending}
            className="block w-full text-sm text-neutral-400
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-semibold
              file:bg-outfront-purple-light file:text-outfront-purple
              hover:file:bg-outfront-purple-light
              disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {uploadMutation.isPending && <LoadingSpinner />}
        </div>
      </Card>

      {/* Upload Success */}
      {uploadResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm font-medium text-green-800">
            Successfully loaded {uploadResult.recordsLoaded} of {uploadResult.totalRowsProcessed} rows.
          </p>
          {uploadResult.rowErrors.length > 0 && (
            <div className="mt-2">
              <p className="text-sm font-medium text-yellow-800">Row errors:</p>
              <ul className="list-disc list-inside text-sm text-yellow-700 max-h-40 overflow-y-auto mt-1">
                {uploadResult.rowErrors.map((err, idx) => (
                  <li key={idx}>
                    Row {err.row} — {err.field}: {err.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Upload Error */}
      {uploadError && <ErrorMessage message={uploadError} />}

      {/* Record Count & Page Size */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-neutral-600">
          {count != null ? `${count.toLocaleString()} total records` : ''}
        </p>
        <div className="w-48">
          <Select
            value={String(pageSize)}
            onChange={handlePageSizeChange}
            options={[
              { value: '25', label: '25 per page' },
              { value: '50', label: '50 per page' },
              { value: '100', label: '100 per page' },
            ]}
          />
        </div>
      </div>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">Unique ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">OMS Identifier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">Media Format</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">Market</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">Size</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">Digital</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">Net Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">Active Date</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {records.map((record) => (
                <tr key={record.id} className="hover:bg-neutral-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.unique_id || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.oms_identifier || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.location_description || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.media_format || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.market || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.size || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={record.is_digital ? 'success' : 'secondary'}>
                      {record.is_digital ? 'Yes' : 'No'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                    {record.net_rate != null && !isNaN(Number(record.net_rate)) ? `$${Number(record.net_rate).toFixed(2)}` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                    {record.active_date ? new Date(record.active_date).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {records.length === 0 && (
          <div className="text-center py-8 text-neutral-400">
            No inventory records found.
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between">
            <div className="text-sm text-neutral-600">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}